// A category's "Certificates required" (CR_20260921_Asset_Lifecycle L4, §7.2): configured once for every asset of the
// category — "boring buckets need a lifting-gear certificate every 180 days, blocks dispatch, remind 30 days before".
// The build ships with no rules at all (Q1: which ones are statutory is the customer's safety officer's call).
import {
  Alert,
  Autocomplete,
  Button,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { sameKind } from '@fleetspoke/core/lifecycle';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../auth/session';
import {
  type CertRule,
  useCertKinds,
  useCertRules,
  useDeleteCertRule,
  useSaveCertRule,
} from '../data/certificates';

const days = (v: string) => (v.trim() === '' ? null : Math.max(0, Math.round(Number(v))));

function RuleRow({ rule }: { rule: CertRule }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const save = useSaveCertRule(me!.company!.id);
  const del = useDeleteCertRule();
  const [validFor, setValidFor] = useState(
    rule.valid_for_days == null ? '' : String(rule.valid_for_days),
  );
  const [remind, setRemind] = useState(String(rule.remind_days_before));
  const commit = (patch: Partial<CertRule>) =>
    save.mutate({
      id: rule.id,
      category_id: rule.category_id,
      kind: rule.kind,
      valid_for_days: rule.valid_for_days,
      blocks_dispatch: rule.blocks_dispatch,
      remind_days_before: rule.remind_days_before,
      ...patch,
    });
  return (
    <TableRow data-testid="cert-rule">
      <TableCell sx={{ fontWeight: 500 }}>{rule.kind}</TableCell>
      <TableCell sx={{ width: 130 }}>
        <TextField
          size="small"
          type="number"
          value={validFor}
          onChange={(e) => setValidFor(e.target.value)}
          onBlur={() =>
            days(validFor) !== rule.valid_for_days &&
            commit({ valid_for_days: days(validFor) || null })
          }
          slotProps={{ htmlInput: { min: 1, max: 3650, 'aria-label': t('certs.rule_valid_for') } }}
        />
      </TableCell>
      <TableCell sx={{ width: 110 }}>
        <Switch
          checked={rule.blocks_dispatch}
          onChange={(e) => commit({ blocks_dispatch: e.target.checked })}
          slotProps={{ input: { 'aria-label': t('certs.rule_blocks') } }}
        />
      </TableCell>
      <TableCell sx={{ width: 130 }}>
        <TextField
          size="small"
          type="number"
          value={remind}
          onChange={(e) => setRemind(e.target.value)}
          onBlur={() =>
            days(remind) != null &&
            days(remind) !== rule.remind_days_before &&
            commit({ remind_days_before: Math.min(365, days(remind)!) })
          }
          slotProps={{ htmlInput: { min: 0, max: 365, 'aria-label': t('certs.rule_remind') } }}
        />
      </TableCell>
      <TableCell align="right" sx={{ width: 56 }}>
        <Tooltip title={t('certs.rule_delete')}>
          <IconButton
            size="small"
            aria-label={t('certs.rule_delete')}
            disabled={del.isPending}
            onClick={() => del.mutate(rule.id)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

export function CertRulesTab({ categoryId }: { categoryId: string }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const rules = useCertRules();
  const kinds = useCertKinds();
  const save = useSaveCertRule(me!.company!.id);
  const mine = (rules.data ?? []).filter((r) => r.category_id === categoryId);
  const [kind, setKind] = useState('');
  const [validFor, setValidFor] = useState('');
  const [blocks, setBlocks] = useState(true);
  const [remind, setRemind] = useState('30');
  const taken = mine.some((r) => sameKind(r.kind, kind));
  const add = () =>
    save.mutate(
      {
        category_id: categoryId,
        kind,
        valid_for_days: days(validFor) || null,
        blocks_dispatch: blocks,
        remind_days_before: Math.min(365, days(remind) ?? 30),
      },
      {
        onSuccess: () => {
          setKind('');
          setValidFor('');
          setBlocks(true);
          setRemind('30');
        },
      },
    );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {t('certs.rules_intro')}
      </Typography>
      {mine.length ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('certs.kind')}</TableCell>
              <TableCell>{t('certs.rule_valid_for')}</TableCell>
              <TableCell>{t('certs.rule_blocks')}</TableCell>
              <TableCell>{t('certs.rule_remind')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {mine.map((r) => (
              <RuleRow key={r.id} rule={r} />
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography color="text.secondary" data-testid="cert-rules-none">
          {t('certs.rules_none')}
        </Typography>
      )}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ md: 'center' }}
        sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}
      >
        <Autocomplete
          freeSolo
          options={kinds.data ?? []}
          inputValue={kind}
          onInputChange={(_, v) => setKind(v)}
          sx={{ flex: 1, minWidth: 200 }}
          renderInput={(p) => (
            <TextField
              {...p}
              size="small"
              label={t('certs.kind')}
              error={taken}
              helperText={taken ? t('certs.kind_taken') : undefined}
              slotProps={{
                htmlInput: { ...p.inputProps, maxLength: 40, 'data-testid': 'cert-rule-kind' },
              }}
            />
          )}
        />
        <TextField
          size="small"
          type="number"
          label={t('certs.rule_valid_for')}
          value={validFor}
          onChange={(e) => setValidFor(e.target.value)}
          sx={{ width: { md: 140 } }}
          slotProps={{ htmlInput: { min: 1, max: 3650 } }}
        />
        <TextField
          size="small"
          type="number"
          label={t('certs.rule_remind')}
          value={remind}
          onChange={(e) => setRemind(e.target.value)}
          sx={{ width: { md: 150 } }}
          slotProps={{ htmlInput: { min: 0, max: 365 } }}
        />
        <Tooltip title={t('certs.rule_blocks_help')}>
          <FormControlLabel
            control={<Switch checked={blocks} onChange={(e) => setBlocks(e.target.checked)} />}
            label={t('certs.rule_blocks')}
          />
        </Tooltip>
        <Button
          variant="contained"
          disabled={!kind.trim() || taken || save.isPending}
          onClick={add}
          data-testid="cert-rule-add"
        >
          {t('certs.rule_add')}
        </Button>
      </Stack>
      {save.error ? (
        <Alert severity="error">
          {t('common.error_generic')} {(save.error as Error).message}
        </Alert>
      ) : null}
    </Stack>
  );
}
